// src/components/pos/AnimacionPOSCompacto.js
//
// Animación SVG del POS Compacto: tablet mostrando venta en curso →
// impresión de ticket térmico. Loop infinito de ~8s con crossfade
// entre las 3 escenas. Colores de la marca (verde primario, naranja
// secundario).

import React from 'react';
import { Box } from '@mui/material';

// Paleta HolaVeci
const VERDE      = '#2E7D32';
const VERDE_DARK = '#1B5E20';
const NARANJA    = '#F57C00';
const CREMA      = '#FBF7F0';
const TEXT_DARK  = '#212121';

const AnimacionPOSCompacto = () => (
  <Box sx={{
    width: '100%', maxWidth: 600, mx: 'auto',
    aspectRatio: '4 / 3',
  }}>
    <svg viewBox="0 0 640 480" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
      <defs>
        <linearGradient id="fondoGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%"   stopColor={CREMA}  />
          <stop offset="100%" stopColor="#f0e9d9" />
        </linearGradient>

        <linearGradient id="marcoGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#3a3a3a" />
          <stop offset="100%" stopColor="#1a1a1a" />
        </linearGradient>

        <linearGradient id="headerGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor={VERDE_DARK} />
          <stop offset="100%" stopColor={VERDE} />
        </linearGradient>

        {/* Sombra suave debajo de la tablet */}
        <radialGradient id="sombra" cx="50%" cy="50%" r="50%">
          <stop offset="0%"   stopColor="rgba(0,0,0,0.25)" />
          <stop offset="100%" stopColor="rgba(0,0,0,0)" />
        </radialGradient>
      </defs>

      <style>{`
        /* Loop de 8 segundos con 3 escenas */
        @keyframes escenaVenta {
          0%, 30% { opacity: 1; transform: translateX(0); }
          35%, 100% { opacity: 0; transform: translateX(-20px); }
        }
        @keyframes escenaCobro {
          0%, 30% { opacity: 0; transform: translateX(20px); }
          35%, 60% { opacity: 1; transform: translateX(0); }
          65%, 100% { opacity: 0; transform: translateX(-20px); }
        }
        @keyframes escenaTicket {
          0%, 60% { opacity: 0; transform: translateX(20px); }
          65%, 95% { opacity: 1; transform: translateX(0); }
          100% { opacity: 0; }
        }

        .escena-venta   { animation: escenaVenta   8s ease-in-out infinite; transform-origin: center; }
        .escena-cobro   { animation: escenaCobro   8s ease-in-out infinite; transform-origin: center; }
        .escena-ticket  { animation: escenaTicket  8s ease-in-out infinite; transform-origin: center; }

        /* Item que aparece en el carrito */
        @keyframes itemPop {
          from { opacity: 0; transform: translateY(8px) scale(0.9); }
          to   { opacity: 1; transform: translateY(0)    scale(1); }
        }
        .item-1 { animation: itemPop 0.4s ease-out 0.4s both; }
        .item-2 { animation: itemPop 0.4s ease-out 0.9s both; }
        .item-3 { animation: itemPop 0.4s ease-out 1.4s both; }

        /* Ticket que se desliza saliendo de la impresora */
        @keyframes ticketSalir {
          from { transform: translateY(-90px); }
          to   { transform: translateY(0); }
        }
        .ticket-papel {
          animation: ticketSalir 1.2s ease-out 5s both;
        }

        /* Pulse del botón de cobro */
        @keyframes pulseBoton {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.05); }
        }
        .boton-cobrar {
          animation: pulseBoton 1.5s ease-in-out infinite;
          transform-origin: center;
        }

        /* Sombra del pedestal, respiración suave */
        @keyframes respirar {
          0%, 100% { opacity: 0.35; }
          50%      { opacity: 0.5; }
        }
        .sombra-pedestal { animation: respirar 4s ease-in-out infinite; }
      `}</style>

      {/* Fondo */}
      <rect width="640" height="480" fill="url(#fondoGrad)" />

      {/* Sombra de la tablet */}
      <ellipse cx="320" cy="450" rx="230" ry="14"
               fill="url(#sombra)" className="sombra-pedestal" />

      {/* ═══ MARCO DE LA TABLET ═══ */}
      <rect x="70" y="60" width="500" height="360" rx="20"
            fill="url(#marcoGrad)" />
      {/* Cámara frontal */}
      <circle cx="320" cy="76" r="3" fill="#444" />
      {/* Botón home lateral */}
      <circle cx="580" cy="240" r="4" fill="#333" />

      {/* ═══ PANTALLA ═══ */}
      <rect x="88" y="90" width="464" height="310" rx="6" fill={CREMA} />

      {/* Header de la app (siempre visible) */}
      <rect x="88" y="90" width="464" height="34" rx="6" fill="url(#headerGrad)" />
      <rect x="88" y="112" width="464" height="12" fill={VERDE} />
      <text x="106" y="112" fill="#fff" fontSize="12" fontWeight="700"
            fontFamily="system-ui,-apple-system,sans-serif">HolaVeci Local</text>
      <text x="530" y="112" fill="rgba(255,255,255,0.85)" fontSize="10" textAnchor="end"
            fontFamily="system-ui,-apple-system,sans-serif">Caja #1</text>

      {/* ═══════════════════════════════════════════════════════ */}
      {/* ESCENA 1: VENTA — armar carrito                         */}
      {/* ═══════════════════════════════════════════════════════ */}
      <g className="escena-venta">
        {/* Título */}
        <text x="106" y="146" fill={TEXT_DARK} fontSize="11" fontWeight="600"
              fontFamily="system-ui,sans-serif">Buscar producto…</text>
        <rect x="106" y="152" width="300" height="20" rx="3" fill="#fff" stroke="#ddd" />
        <rect x="420" y="150" width="115" height="24" rx="4" fill={VERDE} />
        <text x="477" y="166" fill="#fff" fontSize="10" textAnchor="middle" fontWeight="600"
              fontFamily="system-ui,sans-serif">+ Escanear</text>

        {/* Items del carrito */}
        <g className="item-1">
          <rect x="106" y="185" width="429" height="32" rx="4" fill="#fff" stroke="#e6e0d0" />
          <circle cx="122" cy="201" r="10" fill={VERDE} opacity="0.15" />
          <text x="122" y="205" fill={VERDE_DARK} fontSize="10" fontWeight="700" textAnchor="middle"
                fontFamily="system-ui,sans-serif">🥑</text>
          <text x="145" y="199" fill={TEXT_DARK} fontSize="11" fontWeight="600"
                fontFamily="system-ui,sans-serif">Aguacate hass</text>
          <text x="145" y="211" fill="#777" fontSize="9"
                fontFamily="system-ui,sans-serif">2 × $3.500</text>
          <text x="525" y="205" fill={TEXT_DARK} fontSize="11" fontWeight="700" textAnchor="end"
                fontFamily="system-ui,sans-serif">$7.000</text>
        </g>

        <g className="item-2">
          <rect x="106" y="225" width="429" height="32" rx="4" fill="#fff" stroke="#e6e0d0" />
          <circle cx="122" cy="241" r="10" fill={VERDE} opacity="0.15" />
          <text x="122" y="245" fill={VERDE_DARK} fontSize="10" fontWeight="700" textAnchor="middle"
                fontFamily="system-ui,sans-serif">🍞</text>
          <text x="145" y="239" fill={TEXT_DARK} fontSize="11" fontWeight="600"
                fontFamily="system-ui,sans-serif">Pan tajado familiar</text>
          <text x="145" y="251" fill="#777" fontSize="9"
                fontFamily="system-ui,sans-serif">1 × $6.200</text>
          <text x="525" y="245" fill={TEXT_DARK} fontSize="11" fontWeight="700" textAnchor="end"
                fontFamily="system-ui,sans-serif">$6.200</text>
        </g>

        <g className="item-3">
          <rect x="106" y="265" width="429" height="32" rx="4" fill="#fff" stroke="#e6e0d0" />
          <circle cx="122" cy="281" r="10" fill={VERDE} opacity="0.15" />
          <text x="122" y="285" fill={VERDE_DARK} fontSize="10" fontWeight="700" textAnchor="middle"
                fontFamily="system-ui,sans-serif">🥛</text>
          <text x="145" y="279" fill={TEXT_DARK} fontSize="11" fontWeight="600"
                fontFamily="system-ui,sans-serif">Leche entera 1L</text>
          <text x="145" y="291" fill="#777" fontSize="9"
                fontFamily="system-ui,sans-serif">1 × $4.800</text>
          <text x="525" y="285" fill={TEXT_DARK} fontSize="11" fontWeight="700" textAnchor="end"
                fontFamily="system-ui,sans-serif">$4.800</text>
        </g>

        {/* Total */}
        <rect x="106" y="325" width="429" height="55" rx="6" fill={VERDE_DARK} />
        <text x="120" y="345" fill={CREMA} fontSize="10" opacity="0.8"
              fontFamily="system-ui,sans-serif">TOTAL</text>
        <text x="120" y="368" fill="#fff" fontSize="22" fontWeight="800"
              fontFamily="system-ui,sans-serif">$18.000</text>
        <rect x="425" y="338" width="100" height="30" rx="15" fill={NARANJA}
              className="boton-cobrar" />
        <text x="475" y="358" fill="#fff" fontSize="12" fontWeight="700" textAnchor="middle"
              fontFamily="system-ui,sans-serif">Cobrar →</text>
      </g>

      {/* ═══════════════════════════════════════════════════════ */}
      {/* ESCENA 2: COBRO — selección de método                   */}
      {/* ═══════════════════════════════════════════════════════ */}
      <g className="escena-cobro">
        <text x="320" y="150" fill={TEXT_DARK} fontSize="12" fontWeight="700" textAnchor="middle"
              fontFamily="system-ui,sans-serif">Método de pago</text>

        <rect x="120" y="165" width="180" height="70" rx="8" fill={VERDE} />
        <text x="210" y="196" fill="#fff" fontSize="24" textAnchor="middle"
              fontFamily="system-ui,sans-serif">💵</text>
        <text x="210" y="222" fill="#fff" fontSize="11" fontWeight="600" textAnchor="middle"
              fontFamily="system-ui,sans-serif">Efectivo</text>

        <rect x="340" y="165" width="180" height="70" rx="8" fill="#fff" stroke="#ccc" strokeWidth="1.5" />
        <text x="430" y="196" fill={TEXT_DARK} fontSize="24" textAnchor="middle"
              fontFamily="system-ui,sans-serif">💳</text>
        <text x="430" y="222" fill={TEXT_DARK} fontSize="11" fontWeight="600" textAnchor="middle"
              fontFamily="system-ui,sans-serif">Tarjeta</text>

        <rect x="120" y="250" width="180" height="70" rx="8" fill="#fff" stroke="#ccc" strokeWidth="1.5" />
        <text x="210" y="281" fill={TEXT_DARK} fontSize="24" textAnchor="middle"
              fontFamily="system-ui,sans-serif">📱</text>
        <text x="210" y="307" fill={TEXT_DARK} fontSize="11" fontWeight="600" textAnchor="middle"
              fontFamily="system-ui,sans-serif">Transferencia</text>

        <rect x="340" y="250" width="180" height="70" rx="8" fill="#fff" stroke="#ccc" strokeWidth="1.5" />
        <text x="430" y="281" fill={TEXT_DARK} fontSize="24" textAnchor="middle"
              fontFamily="system-ui,sans-serif">📓</text>
        <text x="430" y="307" fill={TEXT_DARK} fontSize="11" fontWeight="600" textAnchor="middle"
              fontFamily="system-ui,sans-serif">Fiado</text>

        {/* Cambio calculado */}
        <text x="320" y="356" fill={TEXT_DARK} fontSize="10" textAnchor="middle" opacity="0.7"
              fontFamily="system-ui,sans-serif">Recibido $20.000 · Cambio</text>
        <text x="320" y="382" fill={VERDE_DARK} fontSize="20" fontWeight="800" textAnchor="middle"
              fontFamily="system-ui,sans-serif">$2.000</text>
      </g>

      {/* ═══════════════════════════════════════════════════════ */}
      {/* ESCENA 3: TICKET IMPRESO                                */}
      {/* ═══════════════════════════════════════════════════════ */}
      <g className="escena-ticket">
        <text x="320" y="150" fill={VERDE_DARK} fontSize="14" fontWeight="700" textAnchor="middle"
              fontFamily="system-ui,sans-serif">✓ Venta registrada</text>

        {/* Impresora térmica */}
        <rect x="220" y="170" width="200" height="45" rx="4" fill="#333" />
        <rect x="240" y="180" width="160" height="18" rx="2" fill="#1a1a1a" />
        <text x="320" y="192" fill="#666" fontSize="8" textAnchor="middle"
              fontFamily="system-ui,sans-serif">TERMICA 80mm</text>
        {/* Ranura de salida del papel */}
        <rect x="235" y="212" width="170" height="3" fill="#1a1a1a" />

        {/* Ticket papel que sale */}
        <g clipPath="url(#recorteTicket)">
          <defs>
            <clipPath id="recorteTicket">
              <rect x="240" y="215" width="160" height="170" />
            </clipPath>
          </defs>
          <g className="ticket-papel">
            <rect x="245" y="220" width="150" height="160" fill="#fff" stroke="#ddd" />
            <text x="320" y="234" fontSize="8" fontWeight="700" textAnchor="middle" fill={TEXT_DARK}
                  fontFamily="system-ui,sans-serif">TIENDA LA ESQUINA</text>
            <text x="320" y="245" fontSize="6" textAnchor="middle" fill="#666"
                  fontFamily="system-ui,sans-serif">NIT 900123456-7</text>
            <line x1="255" y1="252" x2="385" y2="252" stroke="#ddd" strokeDasharray="1,1" />

            <text x="255" y="264" fontSize="6" fill={TEXT_DARK}
                  fontFamily="monospace,monospace">Aguacate x2</text>
            <text x="385" y="264" fontSize="6" fill={TEXT_DARK} textAnchor="end"
                  fontFamily="monospace,monospace">$7.000</text>
            <text x="255" y="274" fontSize="6" fill={TEXT_DARK}
                  fontFamily="monospace,monospace">Pan tajado</text>
            <text x="385" y="274" fontSize="6" fill={TEXT_DARK} textAnchor="end"
                  fontFamily="monospace,monospace">$6.200</text>
            <text x="255" y="284" fontSize="6" fill={TEXT_DARK}
                  fontFamily="monospace,monospace">Leche 1L</text>
            <text x="385" y="284" fontSize="6" fill={TEXT_DARK} textAnchor="end"
                  fontFamily="monospace,monospace">$4.800</text>

            <line x1="255" y1="292" x2="385" y2="292" stroke="#ddd" strokeDasharray="1,1" />
            <text x="255" y="305" fontSize="9" fill={TEXT_DARK} fontWeight="700"
                  fontFamily="monospace,monospace">TOTAL</text>
            <text x="385" y="305" fontSize="10" fill={TEXT_DARK} fontWeight="700" textAnchor="end"
                  fontFamily="monospace,monospace">$18.000</text>

            <text x="320" y="322" fontSize="6" textAnchor="middle" fill="#666"
                  fontFamily="system-ui,sans-serif">Pagado en efectivo</text>
            <line x1="255" y1="330" x2="385" y2="330" stroke="#ddd" strokeDasharray="1,1" />
            <text x="320" y="345" fontSize="7" textAnchor="middle" fill={VERDE_DARK} fontWeight="600"
                  fontFamily="system-ui,sans-serif">¡Gracias por su compra!</text>
            <text x="320" y="360" fontSize="5" textAnchor="middle" fill="#999"
                  fontFamily="system-ui,sans-serif">Ticket #001284</text>
          </g>
        </g>
      </g>
    </svg>
  </Box>
);

export default AnimacionPOSCompacto;
